import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Chatbot.module.scss';
import { answer, BotReply, replyFor } from './chatbotEngine';
import { ChatbotEntry, ChatbotLink, useChatbotEntries } from './chatbotService';

type Message = { id: number; from: 'user'; text: string } | ({ id: number; from: 'bot' } & BotReply);

const GREETING: BotReply = {
  text: '안녕하세요, G-WORKS입니다 👋\n궁금하신 내용을 아래 메뉴에서 선택하시거나 직접 입력해 주세요.',
};

// 하단 고정 버튼과 겹치는 화면에서는 숨긴다.
const HIDDEN_PATHS = ['/quote'];

const KAKAO_CHANNEL_URL = 'https://pf.kakao.com/_KQTpj';

function Chatbot() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([{ id: 0, from: 'bot', ...GREETING }]);
  const entries = useChatbotEntries(open);
  const nextId = useRef(1);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (HIDDEN_PATHS.some((path) => location.pathname.startsWith(path))) {
    return null;
  }

  const quickEntries = (entries ?? []).filter((entry) => entry.quickLabel);

  const push = (userText: string, reply: BotReply) => {
    const userId = nextId.current++;
    const botId = nextId.current++;
    setMessages((prev) => [...prev, { id: userId, from: 'user', text: userText }, { id: botId, from: 'bot', ...reply }]);
  };

  const showEntry = (entry: ChatbotEntry, userText = entry.question) => push(userText, replyFor(entry));

  const reset = () => {
    nextId.current = 1;
    setMessages([{ id: 0, from: 'bot', ...GREETING }]);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || !entries) return;
    push(text, answer(text, entries));
    setInput('');
  };

  const renderLink = (link: ChatbotLink) =>
    link.url.startsWith('/') ? (
      <Link key={link.url + link.label} to={link.url} className={styles.linkBtn} onClick={() => setOpen(false)}>
        {link.label}
      </Link>
    ) : (
      <a
        key={link.url + link.label}
        href={link.url}
        className={styles.linkBtn}
        {...(link.url.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {link.label}
      </a>
    );

  const renderBot = (message: Extract<Message, { from: 'bot' }>) => (
    <div className={styles.bubbleBot}>
      {message.entry && <p className={styles.faqQuestion}>Q. {message.entry.question}</p>}
      <p className={styles.plain}>{message.text}</p>
      {!!message.links?.length && <div className={styles.links}>{message.links.map(renderLink)}</div>}
      {message.related && (
        <div className={styles.related}>
          <span className={styles.relatedLabel}>관련 질문</span>
          {message.related.map((entry) => (
            <button key={entry.id} type="button" className={styles.relatedBtn} onClick={() => showEntry(entry)}>
              {entry.question}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {open && (
        <div className={styles.panel} role="dialog" aria-label="G-WORKS 챗봇">
          <div className={styles.header}>
            <div>
              <strong className={styles.title}>G-WORKS 챗봇</strong>
              <span className={styles.subtitle}>자주 묻는 질문을 바로 안내해 드려요</span>
            </div>
            <div className={styles.headerActions}>
              <button type="button" className={styles.iconBtn} onClick={reset} aria-label="처음으로">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M3 12a9 9 0 1 0 3-6.7" />
                  <path d="M3 4v5h5" />
                </svg>
              </button>
              <button type="button" className={styles.iconBtn} onClick={() => setOpen(false)} aria-label="닫기">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.body} ref={bodyRef}>
            {messages.map((message) =>
              message.from === 'user' ? (
                <div key={message.id} className={styles.bubbleUser}>
                  {message.text}
                </div>
              ) : (
                <div key={message.id}>{renderBot(message)}</div>
              ),
            )}
          </div>

          {quickEntries.length > 0 && (
            <div className={styles.quickMenus}>
              {quickEntries.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={styles.chip}
                  onClick={() => showEntry(entry, entry.quickLabel ?? entry.question)}
                >
                  {entry.quickLabel}
                </button>
              ))}
            </div>
          )}

          <form className={styles.inputRow} onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              className={styles.input}
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={entries ? '궁금한 내용을 입력해 주세요' : '불러오는 중...'}
              maxLength={200}
            />
            <button type="submit" className={styles.sendBtn} disabled={!input.trim() || !entries} aria-label="보내기">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12l16-8-6 16-2-7-8-1z" />
              </svg>
            </button>
          </form>
        </div>
      )}

      {!open && (
        <a
          href={KAKAO_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.kakaoFab}
          aria-label="카카오톡 문의 열기 (새 창)"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 3C6.48 3 2 6.54 2 10.9c0 2.83 1.89 5.31 4.73 6.7-.21.77-.75 2.78-.86 3.21-.13.53.2.53.41.38.17-.11 2.7-1.83 3.79-2.58.63.09 1.27.14 1.93.14 5.52 0 10-3.54 10-7.85S17.52 3 12 3z" />
          </svg>
          <span className={styles.fabLabel}>문의</span>
        </a>
      )}

      <button
        type="button"
        className={`${styles.fab}${open ? styles.fabOpen : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? '챗봇 닫기' : '챗봇 열기'}
        aria-expanded={open}
      >
        {open ? (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        ) : (
          <>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 5h16v11H9l-5 4V5z" />
              <path d="M8 10h.01M12 10h.01M16 10h.01" />
            </svg>
            <span className={styles.fabLabel}>챗봇</span>
          </>
        )}
      </button>
    </>
  );
}

export default Chatbot;
